import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import type { Band, BandSearchHit } from '../types';
import { BandRow } from './BandRow';

export interface PickedBand extends Band { top: boolean }

interface Props {
  value: PickedBand[];
  onChange: (next: PickedBand[]) => void;
  maxTop?: number;
  /** false hides the ★ controls (custom search sets where ranking is optional). */
  allowTop?: boolean;
}

const MAX_TOP = 10;

/**
 * Search MusicBrainz, add bands, star up to N as "top" (ranked in the order
 * starred, reorderable with arrows). Emits the full ordered list: top bands
 * first in rank order, then the rest.
 */
export function BandPicker({ value, onChange, maxTop = MAX_TOP, allowTop = true }: Props) {
  const [q, setQ] = useState('');
  const [hits, setHits] = useState<BandSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  const latest = useRef(value);
  latest.current = value;

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    if (q.trim().length < 2) { setHits([]); return; }
    timer.current = window.setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const { results } = await api.searchBands(q.trim());
        setHits(results.filter((h) => !latest.current.some((v) => v.mbid === h.mbid)));
      } catch (e: any) {
        setError(e.message);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [q]);

  const add = async (hit: BandSearchHit) => {
    setQ('');
    setHits([]);
    // Optimistic row; replaced with genres + image once the server has hydrated the band.
    const placeholder: PickedBand = { mbid: hit.mbid, name: hit.name, disambiguation: hit.disambiguation, genres: [], imageUrl: hit.imageUrl, imageUrlBig: null, top: false };
    onChange([...latest.current, placeholder]);
    try {
      const full = await api.band(hit.mbid);
      onChange(latest.current.map((b) => (b.mbid === hit.mbid ? { ...full, top: b.top } : b)));
    } catch { /* keep the placeholder; saving retries hydration server-side */ }
  };

  const remove = (mbid: string) => onChange(value.filter((b) => b.mbid !== mbid));

  const toggleTop = (mbid: string) => {
    const b = value.find((x) => x.mbid === mbid);
    if (!b) return;
    if (!b.top && value.filter((x) => x.top).length >= maxTop) return;
    const rest = value.filter((x) => x.mbid !== mbid);
    const next = { ...b, top: !b.top };
    const tops = rest.filter((x) => x.top);
    const others = rest.filter((x) => !x.top);
    onChange(next.top ? [...tops, next, ...others] : [...tops, ...others, next]);
  };

  const move = (mbid: string, dir: -1 | 1) => {
    const tops = value.filter((x) => x.top);
    const i = tops.findIndex((x) => x.mbid === mbid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= tops.length) return;
    [tops[i], tops[j]] = [tops[j], tops[i]];
    onChange([...tops, ...value.filter((x) => !x.top)]);
  };

  const tops = value.filter((b) => b.top);
  const others = value.filter((b) => !b.top);

  return (
    <div className="stack">
      <div className="dropdown">
        <input type="search" placeholder="Search bands…" value={q} onChange={(e) => setQ(e.target.value)} />
        {(hits.length > 0 || searching) && (
          <div className="menu">
            {searching && <button type="button" disabled>Searching…</button>}
            {hits.map((h) => (
              <button type="button" key={h.mbid} onClick={() => add(h)}>
                <strong>{h.name}</strong>
                <span className="muted small"> {[h.type, h.country, h.disambiguation].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {error && <div className="error">{error}</div>}

      {allowTop && tops.length > 0 && (
        <div>
          <div className="muted small" style={{ marginBottom: 6 }}>Top bands</div>
          <div className="band-list">
            {tops.map((b, i) => (
              <BandRow key={b.mbid} band={b} rank={i + 1} right={
                <div className="band-actions">
                  <button type="button" className="icon-btn" onClick={() => move(b.mbid, -1)} disabled={i === 0} aria-label="Move up">↑</button>
                  <button type="button" className="icon-btn" onClick={() => move(b.mbid, 1)} disabled={i === tops.length - 1} aria-label="Move down">↓</button>
                  <button type="button" className="icon-btn on" onClick={() => toggleTop(b.mbid)} aria-label="Remove from top">★</button>
                  <button type="button" className="icon-btn" onClick={() => remove(b.mbid)} aria-label="Remove">✕</button>
                </div>
              } />
            ))}
          </div>
        </div>
      )}

      {others.length > 0 && (
        <div>
          {allowTop && tops.length > 0 && <div className="muted small" style={{ marginBottom: 6 }}>More bands</div>}
          <div className="band-list">
            {others.map((b) => (
              <BandRow key={b.mbid} band={b} right={
                <div className="band-actions">
                  {allowTop && <button type="button" className="icon-btn" onClick={() => toggleTop(b.mbid)} disabled={tops.length >= maxTop} aria-label="Add to top">☆</button>}
                  <button type="button" className="icon-btn" onClick={() => remove(b.mbid)} aria-label="Remove">✕</button>
                </div>
              } />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
